import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { apiConfigs } from "../../constants/apiConfigs"
import { Gig, Milestone, MilestoneData, MilestoneStep } from "../../types/collaborations/CollaborationTypes"
import { getErrorMessage, isFresh } from "../../utils/helpers"
import { ERROR } from "../../constants/errors"
import { fetchCollaborationMilestone } from "../../services/api/collaboration"

export interface CollaborationMilestoneSlice {
    loading: boolean,
    error: string | null,
    collaborations: Record<string, //CampaignId
        Record<string, // MilestoneId
            {
                data: MilestoneData
                last_fetched_at: number,
                expired: boolean
            }
        >
    >
}

const initialState: CollaborationMilestoneSlice = {
    loading: true,
    error: null,
    collaborations: {}
}

const STALE_TIME = apiConfigs.FETCH_COLLABORATION_MILESTONE.staleTime;

export const fetchCollaborationMilestoneData = createAsyncThunk(
    'collaboration/milestone',
    async ({ campaignId, milestoneId, force = false }: { campaignId: string, milestoneId: string, force?: boolean }, { getState, rejectWithValue }) => {
        const state = getState() as { collaborationMilestones: CollaborationMilestoneSlice };
        if (!force && isFresh(state.collaborationMilestones.collaborations[campaignId]?.[milestoneId]?.last_fetched_at ?? null, STALE_TIME)) {
            return { campaignId, milestoneId, data: state.collaborationMilestones.collaborations[campaignId][milestoneId].data, cache: true };
        }

        try {
            const data = await fetchCollaborationMilestone(campaignId, milestoneId);
            return {campaignId, milestoneId, data }
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch collaboration milestone'));
        }
    }
)


const collaborationMilestones = createSlice({
    name: 'collaborationMilestones',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
        .addCase(fetchCollaborationMilestoneData.pending, (state) => {
            state.error = null;
            state.loading = true;
        })
        .addCase(fetchCollaborationMilestoneData.fulfilled, (state, {payload}) => {
            const {campaignId, milestoneId, data, cache} = payload;
            state.collaborations[campaignId] = {
                ...(state.collaborations[campaignId] ?? {}),
                [milestoneId]: {
                    ...(state.collaborations[campaignId]?.[milestoneId] ?? {}),
                    data,
                    ...(cache ? {} : {last_fetched_at: Date.now()}),
                    expired: false,
                }
            }
        })
    }
})

export default collaborationMilestones.reducer
